import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import type { Contact, Sequence } from "@/lib/types";
import { useState } from "react";
import {
	Search,
	UserPlus,
	Mail,
	Phone,
	Building2,
	Linkedin,
	Filter,
} from "lucide-react";

interface ContactsProps {
	contacts: Contact[];
	sequences: Sequence[];
}

const statusConfig: Record<string, { label: string; className: string }> = {
	active: { label: "Active", className: "bg-neon-cyan/15 text-neon-cyan border-neon-cyan/30" },
	replied: { label: "Replied", className: "bg-neon-green/15 text-neon-green border-neon-green/30" },
	bounced: { label: "Bounced", className: "bg-neon-pink/15 text-neon-pink border-neon-pink/30" },
	"opted-out": { label: "Opted Out", className: "bg-muted text-muted-foreground border-border" },
};

function getInitials(name: string) {
	return name
		.split(" ")
		.map((n) => n[0])
		.join("")
		.toUpperCase()
		.slice(0, 2);
}

const avatarColors = [
	"bg-neon-cyan/20 text-neon-cyan",
	"bg-neon-purple/20 text-neon-purple",
	"bg-neon-green/20 text-neon-green",
	"bg-neon-pink/20 text-neon-pink",
	"bg-neon-blue/20 text-neon-blue",
];

export function Contacts({ contacts, sequences }: ContactsProps) {
	const [search, setSearch] = useState("");
	const [filterStatus, setFilterStatus] = useState<string | null>(null);

	const filtered = contacts.filter((c) => {
		const matchesSearch =
			!search ||
			c.name.toLowerCase().includes(search.toLowerCase()) ||
			c.email.toLowerCase().includes(search.toLowerCase()) ||
			c.company.toLowerCase().includes(search.toLowerCase());
		const matchesStatus = !filterStatus || c.status === filterStatus;
		return matchesSearch && matchesStatus;
	});

	const sequenceMap = Object.fromEntries(sequences.map((s) => [s.id, s.name]));

	return (
		<div className="p-6 space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold">Contacts</h1>
					<p className="text-muted-foreground text-sm mt-1">
						{contacts.length} contacts across all sequences
					</p>
				</div>
				<Button className="bg-neon-cyan text-black font-semibold hover:bg-neon-cyan/90">
					<UserPlus className="size-4 mr-2" />
					Add Contact
				</Button>
			</div>

			{/* Search & Filters */}
			<div className="flex items-center gap-3">
				<div className="relative flex-1 max-w-sm">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
					<Input
						placeholder="Search contacts..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-9 bg-secondary border-border"
					/>
				</div>
				<div className="flex items-center gap-1.5">
					<Filter className="size-4 text-muted-foreground" />
					<button
						onClick={() => setFilterStatus(null)}
						className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
							!filterStatus ? "bg-neon-cyan/10 text-neon-cyan" : "text-muted-foreground hover:text-foreground"
						}`}
					>
						All
					</button>
					{Object.entries(statusConfig).map(([key, val]) => (
						<button
							key={key}
							onClick={() => setFilterStatus(filterStatus === key ? null : key)}
							className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
								filterStatus === key ? "bg-neon-cyan/10 text-neon-cyan" : "text-muted-foreground hover:text-foreground"
							}`}
						>
							{val.label}
						</button>
					))}
				</div>
			</div>

			{/* Contact List */}
			<div className="space-y-2">
				{filtered.map((contact, i) => {
					const status = statusConfig[contact.status];
					return (
						<Card key={contact.id} className="border-border/50 hover:border-border transition-colors">
							<CardContent className="py-3 px-4">
								<div className="flex items-center gap-4">
									{/* Avatar */}
									<Avatar className={`size-10 ${avatarColors[i % avatarColors.length]}`}>
										<AvatarFallback className="bg-transparent text-sm font-semibold">
											{getInitials(contact.name)}
										</AvatarFallback>
									</Avatar>

									{/* Info */}
									<div className="flex-1 min-w-0">
										<div className="flex items-center gap-2">
											<span className="font-medium">{contact.name}</span>
											<Badge variant="outline" className={`text-xs ${status.className}`}>
												{status.label}
											</Badge>
										</div>
										<div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
											<span className="flex items-center gap-1">
												<Building2 className="size-3" />
												{contact.title} at {contact.company}
											</span>
										</div>
									</div>

									{/* Contact Info */}
									<div className="flex items-center gap-2 shrink-0">
										<div className="flex items-center gap-1 text-xs text-muted-foreground">
											<Mail className="size-3" />
											{contact.email}
										</div>
										{contact.phone && (
											<div className="flex items-center gap-1 text-xs text-muted-foreground">
												<Phone className="size-3" />
												{contact.phone}
											</div>
										)}
										{contact.linkedinUrl && (
											<Linkedin className="size-3 text-neon-blue" />
										)}
									</div>

									{/* Sequence Info */}
									<div className="text-right shrink-0 w-36">
										{contact.sequenceId && (
											<>
												<p className="text-xs text-muted-foreground truncate">
													{sequenceMap[contact.sequenceId]}
												</p>
												{contact.currentStep && (
													<p className="text-xs text-neon-cyan">
														Step {contact.currentStep}
													</p>
												)}
											</>
										)}
									</div>

									{/* Last Contacted */}
									<div className="text-right shrink-0 w-20">
										{contact.lastContactedAt && (
											<p className="text-xs text-muted-foreground">
												{contact.lastContactedAt}
											</p>
										)}
									</div>
								</div>
							</CardContent>
						</Card>
					);
				})}
			</div>
		</div>
	);
}
